import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClock,
  IconExternalLink,
  IconEye,
  IconPlus,
  IconTargetArrow,
  IconTrash,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import eventTypes from '../../constants/eventTypes';
import { MAX_GOALS, newGoal } from '../../helpers/campaignGoals';
import isTransactionalScopeDisabled from '../../helpers/klaviyoTransactional';
import useFetch from '../../helpers/useFetch';
import DatePicker from '../DatePicker';
import ErrorMessage from '../ErrorMessage';
import EventSelector from '../EventSelector';
import GoogleAssetGroupConfigurationTab from '../Google/Ads/GoogleAssetGroupConfigurationTab';
import ScheduleSelector from '../ScheduleSelector';
import SegmentSelector from '../Segments/SegmentSelector';
import TagsSelect from '../TagSelector';

const CampaignFormInner = ({
  campaign,
  handleChange,
  tab,
  setTab,
  mainContent,
  segmentScope,
  contentNameCol = 2,
  isCampaignEditLocked,
  googleNodeConnected,
  onAssetValidationChange,
}) => {
  const fetch = useFetch();
  const setCampaignGoals = (nextGoals) => handleChange({ target: { name: 'goals', value: nextGoals } });
  const updateGoal = (index, key, value) =>
    setCampaignGoals((campaign.goals || []).map((goal, idx) => (idx === index ? { ...goal, [key]: value } : goal)));
  const addGoal = () => setCampaignGoals([...(campaign.goals || []), newGoal()]);
  const removeGoal = (index) => setCampaignGoals((campaign.goals || []).filter((_, idx) => idx !== index));
  const [loadError, setLoadError] = useState();
  const [userError, setUserError] = useState();
  const [segments, setSegments] = useState();
  const [eventActions, setEventActions] = useState();
  const [eventTags, setEventTags] = useState();
  const [cooldownUnit, setCooldownUnit] = useState(campaign?.reentryCooldownInHours > 0 ? 'hours' : 'days');
  // Warning-only, no loading gate: null means "not measured / fail open" and simply suppresses the
  // conversion-tracking warning (unlike GoogleAdNewForm, which uses undefined to gate a spinner).
  const [conversionReadiness, setConversionReadiness] = useState(null);
  const { accountId, domainId } = useParams();
  const customerId = useSelector((state) => state.google.account?.adAccountId);
  // Klaviyo-connected domains can't send transactional email until Klaviyo approves the
  // account - see helpers/klaviyoTransactional for the full rationale and the loading gate.
  const transactionalScopeDisabled = useSelector((state) => isTransactionalScopeDisabled(state.klaviyo));

  const isGoogleAdCampaign =
    (campaign?.type === 'simple' && segmentScope === 'google') ||
    (campaign?.type === 'workflow' && googleNodeConnected);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=${segmentScope}`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, segmentScope]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => setEventActions(eventActions))
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventTags`)
      .then(({ eventTags }) => setEventTags(eventTags))
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  // Google Ads (PMax) bid on conversions, so check whether the account is conversion-ready and only
  // warn when it isn't. Fail open (leave readiness null) if there's no account or the check errors.
  useEffect(() => {
    if (!isGoogleAdCampaign || !customerId) {
      return;
    }
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/google/${customerId}/conversionReadiness`)
      .then(({ conversionReadiness }) => setConversionReadiness(conversionReadiness))
      .catch(() => setConversionReadiness(null));
  }, [fetch, accountId, domainId, customerId, isGoogleAdCampaign]);

  if (
    campaign === undefined ||
    segments === undefined ||
    eventActions === undefined ||
    segments === undefined ||
    eventTags === undefined ||
    eventTypes === undefined
  ) {
    return <Loader />;
  }

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const onTagsChange = (value) => {
    handleChange({ target: { name: 'trigger.tags', value } });
  };

  const segmentOptions = [];
  for (const segment of segments) {
    segmentOptions.push(
      <option key={segments.indexOf(segment)} value={segment.id}>
        {segment.name}
      </option>
    );
  }

  let scheduleSelector;
  if (campaign.scheduled) {
    if (campaign.mode === 'continuous') {
      scheduleSelector = (
        <ScheduleSelector
          schedule={campaign.schedule}
          updateSchedule={(value) => handleChange({ target: { name: 'schedule', value } })}
        />
      );
    } else {
      scheduleSelector = (
        <DatePicker
          disabled={isCampaignEditLocked}
          append="local time"
          showTimeSelect
          value={campaign.scheduledAt}
          minDate={new Date()}
          placeholder="Set a future date"
          timeIntervals={15}
          onChange={(date) => handleChange({ target: { name: 'scheduledAt', value: date } })}
        ></DatePicker>
      );
    }
  }

  let endDateSelector;
  if (campaign.mode === 'continuous' && campaign.endsAt != null) {
    endDateSelector = (
      <DatePicker
        disabled={isCampaignEditLocked}
        append="local time"
        showTimeSelect
        value={campaign.endsAt}
        minDate={campaign.scheduledAt ? new Date(campaign.scheduledAt) : new Date()}
        placeholder="Set an end date"
        timeIntervals={15}
        onChange={(date) => handleChange({ target: { name: 'endsAt', value: date } })}
      />
    );
  }

  let delayedStartSelector;
  if (campaign.mode === 'continuous' && campaign.scheduledAt != null) {
    delayedStartSelector = (
      <DatePicker
        disabled={campaign.running && !!campaign.startedAt}
        append="local time"
        showTimeSelect
        value={campaign.scheduledAt}
        minDate={new Date()}
        placeholder="Set a future start date"
        timeIntervals={15}
        onChange={(date) => handleChange({ target: { name: 'scheduledAt', value: date } })}
      />
    );
  }

  let modeConfig;

  if (campaign.mode === 'triggered' && campaign.trigger.type === 'event') {
    modeConfig = (
      <Box pt="md" mt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Title order={6} fw={600} mb="sm">
          Trigger Configuration
        </Title>

        <Box mb="md">
          <Text fw={500} mb="xs">
            Event
          </Text>
          <EventSelector
            isMulti={true}
            actions={campaign.trigger.actions || []}
            setActions={(value) => handleChange({ target: { name: 'trigger.actions', value } })}
            allowedEventsForTrigger={true}
          />
          <Box size="xs" c="dimmed" mt="xs">
            Select which user actions will trigger this campaign. Use built-in events like{' '}
            <Text span fw={600}>
              cartAdd
            </Text>{' '}
            or{' '}
            <Text span fw={600}>
              cartCheckoutSuccess
            </Text>
            , or send{' '}
            <Anchor
              target="_blank"
              rel="noreferrer"
              href="https://releva.ai/documentation/third-party-integration-apis/custom-event-api/"
            >
              custom events to Releva <IconExternalLink size={12} />
            </Anchor>
            .
            <Text span fw={600}>
              Note:
            </Text>{' '}
            The campaign only triggers for users in the chosen segment. To trigger for all users, select the{' '}
            <Text span fs="italic">
              Everyone
            </Text>{' '}
            segment.
            <Text span>
              <strong>Note:</strong> If there is an event that you want to trigger by and you do not see it here,
              contact support@releva.ai to discuss adding it to your account.
            </Text>
          </Box>
        </Box>

        <Box>
          <Text fw={500} mb="xs">
            Event Tags
          </Text>
          <MultiSelect
            clearable
            disabled={isCampaignEditLocked}
            value={campaign.trigger.tags}
            data={eventTags.map((t) => ({ label: t, value: t }))}
            onChange={(values) => onTagsChange(values)}
          />
          <Text size="xs" c="dimmed" mt="xs">
            Further filter which events trigger the campaign by specifying tags. Only events with matching tags will
            activate this campaign.{' '}
            <Anchor
              target="_blank"
              rel="noreferrer"
              href="https://releva.ai/documentation/third-party-integration-apis/custom-event-api/"
            >
              Learn about custom events <IconExternalLink size={12} />
            </Anchor>
          </Text>
        </Box>
      </Box>
    );
  }
  const description = (
    <Box size="sm" c="dimmed">
      This determines who will enter your campaign.{' '}
      {campaign.mode === 'custom' ? null : (
        <>
          The segment is evaluated{' '}
          {campaign.mode === 'oneOff'
            ? 'at launch time'
            : 'continuously and users are added to the segment based on the rule below'}
          .
        </>
      )}
      {!isCampaignEditLocked ? null : (
        <Text fw={600} mt="xs">
          Changing the segment of running {campaign.type === 'simple' ? 'campaigns' : 'workflows'} is not allowed.
        </Text>
      )}
    </Box>
  );

  let reentryConfig = '';
  if (campaign.mode !== 'oneOff' && campaign.mode !== 'custom') {
    reentryConfig = (
      <Box pt="md" mt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Title order={6} fw={600} mb="sm">
          Repeat Entry
        </Title>

        <Box mb="md">
          <Group justify="space-between" mb="xs">
            <Text fw={500}>Allow users to enter this campaign multiple times</Text>
            <Switch
              disabled={isCampaignEditLocked}
              checked={campaign.reentrable}
              onChange={(e) => {
                handleChange({
                  target: { name: 'reentrable', value: e.target.checked },
                });
              }}
            />
          </Group>
          <Box mt="xs">
            <Text size="xs" c="dimmed" mb="xs">
              When off, each user can enter this {campaign.type === 'simple' ? 'campaign' : 'workflow'} once — perfect
              for exclusive one-time offers. When on, users can enter multiple times—ideal for recurring reminders like
              abandoned cart notifications.
            </Text>
            <Text size="xs" c="dimmed" mb={0}>
              If enabled, you must set a cooldown period to control how often users can re-enter.
            </Text>
            <Text mt="xs" size="xs" c="dimmed" fs="italic">
              * Campaign entry history is stored according to our data retention policy.
            </Text>
          </Box>
        </Box>

        {campaign.reentrable && (
          <Box>
            <Text fw={500} mb="xs">
              Cooldown Period
            </Text>
            <Group gap="xs">
              <NumberInput
                disabled={!campaign.reentrable}
                name={cooldownUnit === 'hours' ? 'reentryCooldownInHours' : 'reentryCooldownInDays'}
                min={cooldownUnit === 'hours' ? 3 : campaign.mode === 'continuous' ? 1 : 0}
                max={cooldownUnit === 'hours' ? 24 : 365}
                step={1}
                value={cooldownUnit === 'hours' ? campaign.reentryCooldownInHours || 3 : campaign.reentryCooldownInDays}
                onChange={(value) => {
                  if (cooldownUnit === 'hours') {
                    handleChange({ target: { name: 'reentryCooldownInHours', value } });
                  } else {
                    handleChange({ target: { name: 'reentryCooldownInDays', value } });
                  }
                }}
                error={
                  cooldownUnit === 'days' && campaign.mode === 'continuous' && campaign.reentryCooldownInDays < 1
                    ? 'The cooldown period must be between 1 and 365 days.'
                    : cooldownUnit === 'hours' &&
                        (campaign.reentryCooldownInHours < 3 || campaign.reentryCooldownInHours > 24)
                      ? 'The cooldown period must be between 3 and 24 hours.'
                      : null
                }
                style={{ flex: 1 }}
              />
              <Select
                data={[
                  { value: 'days', label: 'day(s)' },
                  { value: 'hours', label: 'hour(s)' },
                ]}
                value={cooldownUnit}
                onChange={(value) => {
                  setCooldownUnit(value);
                  if (value === 'hours') {
                    handleChange({ target: { name: 'reentryCooldownInDays', value: 0 } });
                    handleChange({
                      target: { name: 'reentryCooldownInHours', value: campaign.reentryCooldownInHours || 3 },
                    });
                  } else {
                    handleChange({ target: { name: 'reentryCooldownInHours', value: null } });
                    handleChange({
                      target: { name: 'reentryCooldownInDays', value: campaign.reentryCooldownInDays || 1 },
                    });
                  }
                }}
                allowDeselect={false}
                w={100}
              />
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              {cooldownUnit === 'hours'
                ? 'How many hours must pass before a user can enter this campaign again (3–24).'
                : 'How many days must pass before a user can enter this campaign again.'}
            </Text>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      {isGoogleAdCampaign &&
        conversionReadiness &&
        (conversionReadiness.enabledConversionActions === 0 || conversionReadiness.recentConversions === 0) && (
          <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} mb="md">
            Google Ads (Performance Max) use conversion-based bidding, so they will <strong>not serve</strong> unless
            your Google Ads account has <strong>active conversion tracking that is recording conversions</strong>. With
            no conversion data, Google can&apos;t bid and the campaign gets 0 impressions. Make sure a real conversion
            (e.g. purchase) is set up and firing before launching — a test/placeholder conversion with no recorded
            conversions is not enough.
          </Alert>
        )}
      {isGoogleAdCampaign && campaign.mode === 'oneOff' && (
        <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} mb="md">
          Google Ads work best in a <strong>continuous</strong> {campaign.type === 'simple' ? 'campaign' : 'workflow'}.
          A one-off run uploads the audience only once (members age out after the membership window with no refresh) and
          pauses the Google asset group when the {campaign.type === 'simple' ? 'campaign' : 'workflow'} finishes — so
          Google has little time to learn and serve. Consider switching the mode to continuous.
        </Alert>
      )}
      <Tabs value={tab} onChange={setTab} keepMounted>
        <Tabs.List mb="md">
          <Tabs.Tab value="content" leftSection={<IconEye size={16} />}>
            Content
          </Tabs.Tab>
          <Tabs.Tab value="audience" leftSection={<IconUsersGroup size={16} />} data-tour="camapaignAudienceTab">
            Audience
          </Tabs.Tab>
          {campaign.mode !== 'custom' && (
            <Tabs.Tab
              value="launchConfiguration"
              leftSection={<IconClock size={16} />}
              data-tour="camapaignLaunchConfigurationTab"
            >
              Launch Configuration
            </Tabs.Tab>
          )}
          <Tabs.Tab value="goal" leftSection={<IconTargetArrow size={16} />} data-tour="camapaignGoalTab">
            Goal
          </Tabs.Tab>
          {(campaign.type === 'simple' && segmentScope === 'google') ||
          (campaign.type === 'workflow' && googleNodeConnected) ? (
            <Tabs.Tab value="assetGroupUrlConfiguration">Asset Group Configuration</Tabs.Tab>
          ) : null}
        </Tabs.List>

        <Tabs.Panel value="content">
          <Card withBorder>
            <Card.Section p="md">
              <Grid mb="md" data-tour="addNameToCampaign">
                <Grid.Col span={{ base: 12, sm: contentNameCol }} style={{ display: 'flex', alignItems: 'center' }}>
                  <Text fw={500}>Name</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12 - contentNameCol }}>
                  <TextInput
                    disabled={isCampaignEditLocked}
                    name="name"
                    value={campaign.name}
                    placeholder="e.g. New Arrivals"
                    onChange={(e) => handleChange(e)}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: contentNameCol }} style={{ display: 'flex', alignItems: 'center' }}>
                  <Text fw={500}>Campaign Tags</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12 - contentNameCol }}>
                  <TagsSelect
                    model={campaign}
                    handleChange={handleChange}
                    setUserError={setUserError}
                    isCampaignEditLocked={isCampaignEditLocked}
                  />
                </Grid.Col>
              </Grid>
              {mainContent}
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="audience">
          <Card withBorder>
            <Card.Section p="md">
              <Box mb="md">
                <Title order={5} fw={600} mb="xs">
                  Audience Selection
                </Title>
                <Text size="sm" c="dimmed">
                  Define who will receive this campaign and how they should be targeted.
                </Text>
              </Box>

              <Box>
                {campaign.mode === 'custom' || segmentScope === 'facebook' ? null : (
                  <Box mb="md" data-tour="audienceScope">
                    <Text fw={500} mb="xs">
                      Campaign Scope
                    </Text>
                    <SegmentedControl
                      disabled={!!isCampaignEditLocked}
                      value={campaign.scope ?? ''}
                      onChange={(scope) =>
                        handleChange({
                          target: { name: 'scope', value: scope },
                        })
                      }
                      data={[
                        { label: 'Opt-in Marketing', value: 'marketing' },
                        { label: 'Opt-out Marketing', value: 'optOut' },
                        { label: 'Transactional', value: 'transactional', disabled: transactionalScopeDisabled },
                      ]}
                    />
                    <Box mt="xs">
                      <Text size="xs" c="dimmed" mb="xs">
                        Marketing campaigns respect user subscription preferences. Transactional campaigns do not
                        require opt-in and are sent regardless of subscription status.
                      </Text>
                      {transactionalScopeDisabled && (
                        <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} mb="xs">
                          <Text size="xs">
                            Transactional sending is not approved in your Klaviyo account yet, so transactional
                            campaigns are paused. Approve transactional email in Klaviyo, then use the "Set up send
                            metrics &amp; check transactional status" button in Settings &rarr; Messaging &amp; Ads
                            &rarr; Emails.
                          </Text>
                        </Alert>
                      )}
                      <Alert variant="light" color="gray" icon={<IconAlertCircle size={16} />}>
                        <Text size="xs">
                          <Text span fw={600}>
                            Important:
                          </Text>{' '}
                          Never use transactional scope for marketing messages. Misuse may result in fines under GDPR
                          and other data protection laws. If unsure, select{' '}
                          <Text span fs="italic">
                            Opt-in Marketing
                          </Text>
                          .
                        </Text>
                      </Alert>
                    </Box>
                  </Box>
                )}

                {campaign.mode === 'triggered' && (
                  <Box mb="md">
                    <Text fw={500} mb="xs">
                      Trigger on
                    </Text>
                    <SegmentedControl
                      disabled={!!isCampaignEditLocked}
                      value={campaign.trigger.type ?? ''}
                      onChange={(value) => {
                        handleChange({
                          target: { name: 'trigger.type', value },
                        });

                        if (value === 'webhook') {
                          handleChange({
                            target: {
                              name: 'trigger.actions',
                              value: undefined,
                            },
                          });
                        } else if (value === 'event') {
                          handleChange({
                            target: { name: 'segmentId', value: undefined },
                          });
                        }
                      }}
                      data={[
                        { label: 'Webhook', value: 'webhook' },
                        { label: 'Event', value: 'event' },
                      ]}
                    />
                    <Box mt="xs">
                      <Text size="xs" c="dimmed" mb="xs">
                        <Text span fw={600}>
                          Event
                        </Text>{' '}
                        - profiles will enter this {campaign.type === 'simple' ? 'campaign' : 'workflow'} when a
                        specific event occurs. This is useful when you want to send messages in response to user
                        actions, for ex. after they place an order.
                      </Text>
                      <Text size="xs" c="dimmed" mb={0}>
                        <Text span fw={600}>
                          Webhook
                        </Text>{' '}
                        - profiles from the target segment will enter this{' '}
                        {campaign.type === 'simple' ? 'campaign' : 'workflow'} when a webhook is called from your
                        website/application. (Requires technical integration on your part)
                      </Text>
                    </Box>
                  </Box>
                )}

                {campaign.trigger?.type === 'webhook' || campaign.mode !== 'triggered' ? (
                  <Box mb="md">
                    <Text fw={500} mb="xs">
                      Target Segment
                    </Text>
                    <SegmentSelector
                      disabled={isCampaignEditLocked}
                      description={description}
                      segmentId={campaign.segmentId}
                      onSelect={(segment) =>
                        handleChange({
                          target: { name: 'segmentId', value: segment?.id },
                        })
                      }
                      scope={segmentScope}
                      campaignScope={campaign.scope}
                      dataTour="audienceSegment"
                    />
                  </Box>
                ) : (
                  <Box mb="md">
                    <Text fw={500} mb="xs">
                      Target Segment
                    </Text>
                    <Alert variant="light" color="gray" icon={<IconAlertCircle size={16} />}>
                      <Text size="xs">
                        <Text span fw={600}>
                          Important:
                        </Text>{' '}
                        Event-based triggered {campaign.type === 'simple' ? 'campaigns' : ' workflows'} do not support
                        segment filtering. If you wish to limit the audience of this{' '}
                        {campaign.type === 'simple' ? 'campaign' : ' workflow'}, please add a Condition step in the
                        Content section before your messaging steps.
                      </Text>
                    </Alert>
                  </Box>
                )}

                {reentryConfig}
              </Box>
            </Card.Section>
          </Card>
        </Tabs.Panel>

        {campaign.mode !== 'custom' && (
          <Tabs.Panel value="launchConfiguration">
            <Card withBorder>
              <Card.Section p="md">
                <Box mb="md">
                  <Title order={5} fw={600} mb="xs">
                    Launch Settings
                  </Title>
                  <Text size="sm" c="dimmed">
                    Configure when and how your campaign will be executed.
                  </Text>
                </Box>
                <Box>
                  <Box mb="md" data-tour="delayedStart">
                    <Group justify="space-between" mb="xs">
                      <Text fw={500}>Scheduled Execution</Text>
                      <Switch
                        disabled={isCampaignEditLocked}
                        checked={campaign.scheduled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          handleChange({
                            target: {
                              name: 'scheduled',
                              value: checked,
                            },
                          });
                          // Outside continuous mode scheduledAt *is* this switch's date, and the
                          // send jobs honour scheduledAt even when scheduled is false - so switching
                          // off must clear it (null, not undefined, so the PUT actually unsets it).
                          // Continuous keeps scheduledAt: it belongs to the Delayed Start switch.
                          if (!checked && campaign.mode !== 'continuous') {
                            handleChange({ target: { name: 'scheduledAt', value: null } });
                          }
                        }}
                      />
                    </Group>
                    {scheduleSelector && campaign.scheduled && <Box mt="sm">{scheduleSelector}</Box>}
                    <Text size="xs" c="dimmed" mt="xs">
                      {!isCampaignEditLocked ? (
                        <>
                          Schedule a future start time for your campaign. Prepare your promotional campaign in advance
                          and have it launch automatically at the specified time. Simply set the start date, then{' '}
                          {campaign.mode === 'oneOff' ? 'launch' : 'enable'} your campaign—it will wait until the
                          scheduled time before sending messages.
                        </>
                      ) : (
                        <Text fw={600}>
                          Changing the scheduled start of a launched{' '}
                          {campaign.type === 'simple' ? 'campaign' : 'workflow'} is not allowed. To reschedule, please
                          abort it first (if not finished), then create a copy.
                        </Text>
                      )}
                    </Text>
                  </Box>

                  {campaign.mode === 'continuous' && (
                    <Box pt="md" mt="md" mb="md">
                      <Group justify="space-between" mb="xs">
                        <Text fw={500}>Delayed Start</Text>
                        <Switch
                          disabled={campaign.running && !!campaign.startedAt}
                          checked={campaign.scheduledAt != null}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              handleChange({
                                target: {
                                  name: 'scheduledAt',
                                  value: tomorrow,
                                },
                              });
                            } else {
                              handleChange({
                                target: { name: 'scheduledAt', value: null },
                              });
                            }
                          }}
                        />
                      </Group>
                      {delayedStartSelector && <Box mt="sm">{delayedStartSelector}</Box>}
                      <Text size="xs" c="dimmed" mt="xs">
                        {campaign.running && !!campaign.startedAt ? (
                          <Text fw={600}>The start time cannot be changed while the campaign is actively running.</Text>
                        ) : (
                          <>
                            Schedule a future start time. The campaign will not begin processing users until the
                            specified date.
                          </>
                        )}
                      </Text>
                    </Box>
                  )}

                  {campaign.mode === 'continuous' && (
                    <Box pt="md" mt="md" mb="md">
                      <Group justify="space-between" mb="xs">
                        <Text fw={500}>End Date</Text>
                        <Switch
                          disabled={isCampaignEditLocked}
                          checked={campaign.endsAt != null}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const nextMonth = new Date();
                              nextMonth.setMonth(nextMonth.getMonth() + 1);
                              handleChange({
                                target: { name: 'endsAt', value: nextMonth },
                              });
                            } else {
                              handleChange({
                                target: { name: 'endsAt', value: null },
                              });
                            }
                          }}
                        />
                      </Group>
                      {endDateSelector && <Box mt="sm">{endDateSelector}</Box>}
                      <Text size="xs" c="dimmed" mt="xs">
                        Set a date when the campaign will stop processing users.
                      </Text>
                    </Box>
                  )}

                  {segmentScope !== 'facebook' && segmentScope !== 'google' && (
                    <Box pt="md" mb="md">
                      <Group justify="space-between" mb="xs">
                        <Text fw={500}>Send at Optimal Time</Text>
                        <Switch
                          disabled={isCampaignEditLocked}
                          checked={campaign.useOptimalTime}
                          onChange={(e) =>
                            handleChange({
                              target: {
                                name: 'useOptimalTime',
                                value: e.target.checked,
                              },
                            })
                          }
                        />
                      </Group>
                      <Box size="xs" c="dimmed" mt="xs">
                        {!isCampaignEditLocked ? (
                          <>
                            Send messages when each user is most likely to engage with them.
                            {campaign.mode === 'oneOff' && (
                              <Text mt="xs" fs="italic">
                                Note: Messages may be sent over a 24-hour period. This setting will override any
                                shop-wide quiet hours for this {campaign.type === 'simple' ? 'campaign' : 'workflow'}.
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text fw={600}>
                            This setting cannot be modified while the{' '}
                            {campaign.type === 'simple' ? 'campaign' : 'workflow'} is running. Please terminate it
                            first, then create a duplicate to make changes.
                          </Text>
                        )}
                      </Box>
                    </Box>
                  )}

                  {modeConfig}
                </Box>
              </Card.Section>
            </Card>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="goal" data-tour="campaignGoalSettings">
          <Card withBorder>
            <Card.Section p="md">
              <Box mb="md">
                <Title order={5} fw={600} mb="xs">
                  Campaign Goals
                </Title>
                <Text size="sm" c="dimmed">
                  Define what success looks like for this campaign. Each goal is tracked and reported independently in
                  analytics (up to {MAX_GOALS} goals per campaign).
                </Text>
              </Box>
              <Stack gap="md">
                {(campaign.goals || []).map((goal, index) => {
                  const trimmedName = goal.name?.trim();
                  const isDuplicate =
                    !!trimmedName &&
                    (campaign.goals || []).some(
                      (other, otherIdx) => otherIdx !== index && other.name?.trim() === trimmedName
                    );
                  const nameError = !trimmedName
                    ? 'Goal name is required'
                    : isDuplicate
                      ? 'Goal names must be unique'
                      : null;
                  return (
                    <Card
                      key={goal.id ?? index}
                      withBorder
                      p="md"
                      style={{ background: 'var(--mantine-color-gray-0)' }}
                    >
                      <Group justify="space-between" mb="md">
                        <Text fw={600}>Goal {index + 1}</Text>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={isCampaignEditLocked || (campaign.goals || []).length <= 1}
                          onClick={() => removeGoal(index)}
                          aria-label={`Remove goal ${index + 1}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>

                      <Box mb="md">
                        <Text fw={500} mb="xs">
                          Goal Name
                        </Text>
                        <TextInput
                          disabled={isCampaignEditLocked}
                          value={goal.name}
                          placeholder="e.g., Purchases, Sign-ups, Downloads"
                          onChange={(e) => updateGoal(index, 'name', e.target.value)}
                          error={nameError}
                        />
                        <Text size="xs" c="dimmed" mt="xs">
                          Choose a clear, descriptive name that will appear in your dashboard
                        </Text>
                      </Box>

                      <Box mb="md">
                        <Text fw={500} mb="xs">
                          Goal Events
                        </Text>
                        <EventSelector
                          actions={goal.actions}
                          isMulti={true}
                          setActions={(values) => updateGoal(index, 'actions', values)}
                        />
                        <Text size="xs" c="dimmed" mt="xs">
                          Select which events count as achieving this goal
                        </Text>
                      </Box>

                      <Box>
                        <Group
                          justify="space-between"
                          p="md"
                          style={{
                            background: 'var(--mantine-color-gray-1)',
                            borderRadius: 'var(--mantine-radius-default)',
                          }}
                        >
                          <Box>
                            <Text fw={500} mb="xs">
                              Revenue Tracking
                            </Text>
                            <Text size="sm" c="dimmed">
                              Monitor monetary value associated with this goal's completions
                            </Text>
                          </Box>
                          <Switch
                            checked={!!goal.trackRevenue}
                            onChange={(e) => updateGoal(index, 'trackRevenue', e.target.checked)}
                          />
                        </Group>
                      </Box>
                    </Card>
                  );
                })}

                <Group>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    disabled={isCampaignEditLocked || (campaign.goals || []).length >= MAX_GOALS}
                    onClick={addGoal}
                  >
                    Add goal
                  </Button>
                  <Text size="xs" c="dimmed">
                    {(campaign.goals || []).length} / {MAX_GOALS}
                  </Text>
                </Group>
              </Stack>
            </Card.Section>
          </Card>
        </Tabs.Panel>

        {(campaign.type === 'simple' && segmentScope === 'google') ||
        (campaign.type === 'workflow' && googleNodeConnected) ? (
          <Tabs.Panel value="assetGroupUrlConfiguration">
            <GoogleAssetGroupConfigurationTab
              campaign={campaign}
              handleChange={handleChange}
              onAssetValidationChange={onAssetValidationChange}
            />
          </Tabs.Panel>
        ) : null}
      </Tabs>
      {userError ? (
        <Box ta="right">
          <ErrorMessage message={userError.message} />
        </Box>
      ) : null}
    </>
  );
};

export default CampaignFormInner;
